import { NumberField } from "@/components/ui/number-field";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiRequest } from "@/lib/queryClient";
import type { Habit } from "@shared/schema";
import { AlertCircle } from "lucide-react";

type HabitWithCount = Habit & { completedCount: number };

interface HabitCompletionAlertProps {
  habits: HabitWithCount[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function HabitCompletionAlert({ habits, open, onOpenChange }: HabitCompletionAlertProps) {
  const queryClient = useQueryClient();
  const [showExtendInput, setShowExtendInput] = useState<{ [key: string]: boolean }>({});
  const [extendValue, setExtendValue] = useState<{ [key: string]: number }>({});

  const extendHabitMutation = useMutation({
    mutationFn: async ({ habitId, newTarget }: { habitId: string; newTarget: number }) => {
      return apiRequest("PATCH", `/api/habits/${habitId}`, {
        target: newTarget,
      });
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["/api/habits"] });
    },
    onError: () => {
    },
  });

  const dismissHabitsMutation = useMutation({
    mutationFn: async (habitIds: string[]) => {
      return apiRequest("POST", "/api/user/preferences/dismiss-habits", {
        habitIds,
      });
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["/api/user/preferences"] });
      onOpenChange(false);
    },
    onError: () => {
    },
  });

  const handleExtend = async (habit: Habit) => {
    const additionalCheckboxes = extendValue[habit.id] || 0;
    if (additionalCheckboxes <= 0) {
      return;
    }

    const newTarget = habit.target + additionalCheckboxes;
    await extendHabitMutation.mutateAsync({ habitId: habit.id, newTarget });
    setShowExtendInput({ ...showExtendInput, [habit.id]: false });
    setExtendValue({ ...extendValue, [habit.id]: 0 });
  };

  const handleDismissAll = () => {
    const habitIds = habits.map(h => h.id);
    dismissHabitsMutation.mutate(habitIds);
  };

  if (habits.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none" data-testid="dialog-habit-completion-alert">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <AlertCircle className="w-6 h-6 text-amber-500" />
            Habits Nearing Completion
          </DialogTitle>
          <DialogDescription className="text-base">
            The following habits are 3 or fewer checkboxes away from completion. Would you like to extend them?
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-6">
            {habits.map((habit) => {
              const completedCount = habit.completedCount || 0;
              const remaining = habit.target - completedCount;
              
              return (
                <div
                  key={habit.id}
                  className="border rounded-lg p-4 space-y-3"
                  data-testid={`habit-alert-${habit.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h3 className="font-semibold text-lg" data-testid={`text-habit-name-${habit.id}`}>
                        {habit.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {completedCount} / {habit.target} completed ({remaining} remaining)
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Frequency: {habit.frequency}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {!showExtendInput[habit.id] ? (
                      <Button
                        onClick={() => setShowExtendInput({ ...showExtendInput, [habit.id]: true })}
                        variant="default"
                        size="sm"
                        data-testid={`button-extend-${habit.id}`}
                      >
                        Extend
                      </Button>
                    ) : (
                      <div className="flex items-end gap-2 w-full">
                        <div className="flex-1 max-w-xs">
                          <Label htmlFor={`extend-${habit.id}`} className="text-sm">
                            Add checkboxes
                          </Label>
                          <NumberField
                            id={`extend-${habit.id}`}
                            min={1}
                            value={extendValue[habit.id] || undefined}
                            onValueChange={(v) => setExtendValue({ ...extendValue, [habit.id]: v ?? 0 })}
                            placeholder="Enter number"
                            className="mt-1"
                            data-testid={`input-extend-${habit.id}`}
                          />
                        </div>
                        <Button
                          onClick={() => handleExtend(habit)}
                          disabled={!extendValue[habit.id] || extendValue[habit.id] <= 0 || extendHabitMutation.isPending}
                          size="sm"
                          data-testid={`button-confirm-extend-${habit.id}`}
                        >
                          {extendHabitMutation.isPending ? "Extending..." : "Confirm"}
                        </Button>
                        <Button
                          onClick={() => {
                            setShowExtendInput({ ...showExtendInput, [habit.id]: false });
                            setExtendValue({ ...extendValue, [habit.id]: 0 });
                          }}
                          variant="outline"
                          size="sm"
                          data-testid={`button-cancel-extend-${habit.id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            onClick={handleDismissAll}
            variant="outline"
            disabled={dismissHabitsMutation.isPending}
            data-testid="button-dismiss-all"
          >
            {dismissHabitsMutation.isPending ? "Saving..." : "Close and don't ask again"}
          </Button>
          <Button
            onClick={() => onOpenChange(false)}
            variant="default"
            data-testid="button-close"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
